/**
 * Canvas tool definitions for browser-side registration.
 *
 * Exposes the canvas to the agent as three tool classes:
 *   1. render_* (7 tools) — create or update components. Each has a
 *      distinct typed input schema, kept standalone per MCP Principle 2
 *      (schema-complexity split).
 *   2. canvas_info — read-only inventory queries (list, list_components).
 *   3. canvas_manage — state-changing operations consolidated under one
 *      action enum (set_active, clear, close, clear_all, toggle_panel,
 *      remove_component). Destructive annotations apply.
 *
 * Design principles: see shared/first-principles/mcp.md. Highlights:
 *   - Every tool takes a required `why` field (Principle 21).
 *   - Every tool carries MCP annotations (Principle 12).
 *   - Every response includes a `hint` pointing to the next reasonable
 *     action (Principle 1).
 *   - Errors are structured and navigable, not thrown (Principle 11).
 *   - render_chart validates x_key/y_key against data[0] pre-flight
 *     (Principle 22).
 */

import type {
  ComponentTree,
  CanvasUpdateData,
  CanvasCompleteData,
  Canvas,
} from '../types/canvas';

// ─── Dispatch surface ──────────────────────────────────────────────────

/**
 * Full read+write surface that the canvas tool handlers need from
 * ``useCanvas``. The same object is set once by ``useCanvasTools``.
 */
export interface CanvasDispatch {
  handleUpdate: (data: CanvasUpdateData) => void;
  handleComplete: (data: CanvasCompleteData) => void;
  getState: () => {
    canvases: Map<string, Canvas>;
    activeCanvasId: string | null;
    panelVisible: boolean;
  };
  setActiveCanvas: (canvasId: string) => void;
  closeCanvas: (canvasId: string) => void;
  closeComponent: (canvasId: string, componentId: string) => void;
  clearAllCanvases: () => void;
  togglePanel: () => void;
}

let canvasDispatch: CanvasDispatch | null = null;

/** Set the canvas dispatch functions (called by useCanvasTools). */
export function setCanvasDispatch(dispatch: CanvasDispatch | null): void {
  canvasDispatch = dispatch;
}

// ─── Shared helpers ────────────────────────────────────────────────────

/**
 * Default chart palette. Mirrors Chart.tsx's DEFAULT_COLORS so render_chart
 * can echo the exact colours the renderer will apply when `colors` is omitted.
 */
const DEFAULT_CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

/** Generate a short unique ID for a component. */
function generateId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
}

/**
 * Dispatch a component tree to the canvas via update + complete events.
 * Returns a structured error if the canvas isn't mounted.
 */
function dispatchComponent(
  componentTree: ComponentTree,
  componentId: string,
  canvasId: string,
): { ok: true } | StructuredError {
  if (!canvasDispatch) {
    return notMountedError();
  }
  const artifactId = generateId('artifact');
  canvasDispatch.handleUpdate({
    canvasId,
    componentId,
    artifactId,
    jsonTree: componentTree,
    action: 'upsert',
  });
  canvasDispatch.handleComplete({
    canvasId,
    componentId,
    artifactId,
    jsonTree: componentTree,
    action: 'upsert',
  });
  return { ok: true };
}

interface StructuredError {
  status: 'error';
  code: string;
  message: string;
  hint: string;
  context?: Record<string, unknown>;
}

function notMountedError(): StructuredError {
  return {
    status: 'error',
    code: 'canvas_not_mounted',
    message: 'Canvas dispatch is not available — the useCanvasTools hook has not run yet.',
    hint: 'The canvas mounts after the chat page loads. Retry the same call in a moment, or ask the user to reload the page if the issue persists.',
  };
}

/** Shared parameter snippets. */
const whyParam = {
  type: 'string',
  description:
    'Why you are calling this tool — one short sentence explaining your intent. Required on every call for audit and human review.',
};

const componentIdParam = {
  type: 'string',
  description:
    'Unique ID for this component. Omit (or empty) to create a new component; pass an existing ID to update that component in place (idempotent — re-calling with the same ID replaces, no duplicates). Always returned in the response so you can update or remove it later.',
  default: '',
};

const canvasIdParam = {
  type: 'string',
  description:
    'Target canvas. The user has up to 5 named canvases; default "main" is almost always right — only pass something else when the user explicitly asks for a separate surface. Creating a new canvas is implicit: render any component with a fresh canvas_id and the canvas appears.',
  default: 'main',
};

/** Tool definition matching BrowserRpcMethods.registerTool shape. */
export interface CanvasToolDefinition {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
  handler: (params: Record<string, unknown>) => Promise<unknown>;
  fireAndForget: boolean;
  /** Tool category; the server maps it to a disclosure group as fallback. */
  category?: string;
  /** Progressive-disclosure group used by the agent toolkit (spec 089). */
  disclosureGroup?: import('./types').BrowserToolGroup;
  /** Agent slugs allowed to discover this tool. Omit for all agents. */
  visibleToAgents?: string[];
  annotations?: {
    readOnlyHint?: boolean;
    destructiveHint?: boolean;
    idempotentHint?: boolean;
    openWorldHint?: boolean;
  };
  outputSchema?: Record<string, unknown>;
}

// Component-kind annotations used by every render_* tool.
const RENDER_ANNOTATIONS = {
  readOnlyHint: false,
  destructiveHint: false, // update-in-place is not destructive
  idempotentHint: true, // with component_id set
  openWorldHint: false,
};

/** Minimal outputSchema shared by every render_* tool. */
const RENDER_OUTPUT_SCHEMA = {
  type: 'object',
  properties: {
    status: { type: 'string', enum: ['rendered', 'updated', 'error'] },
    kind: { type: 'string' },
    component_id: { type: 'string' },
    canvas_id: { type: 'string' },
    summary: { type: 'string' },
    hint: { type: 'string' },
  },
  required: ['status'],
};

/**
 * Compose a hint tailored to the agent's next reasonable action after a
 * successful render. Always references the component_id so the agent knows
 * how to update or remove this component.
 */
function renderHint(
  kind: string,
  componentId: string,
  canvasId: string,
): string {
  return (
    `Re-call render_${kind} with component_id='${componentId}' to update in place. ` +
    `Remove with canvas_manage(action:'remove_component', component_id:'${componentId}'${canvasId === 'main' ? '' : `, canvas_id:'${canvasId}'`}). ` +
    `Inspect with canvas_info(action:'list_components').`
  );
}

// ─── Tool definitions ──────────────────────────────────────────────────

export function createCanvasToolDefinitions(): CanvasToolDefinition[] {
  return [
    // ── render_card ──────────────────────────────────────────────────
    {
      name: 'render_card',
      category: 'ui-render',
      disclosureGroup: 'browser_render',
      description:
        'Render a Card component in the canvas panel. Use for single-topic highlights (a KPI, a notable finding, a summary). Prefer render_text for long-form content, render_table for tabular data, render_chart for numeric comparisons. Returns { status, component_id, canvas_id, title, variant, content_chars, hint }.',
      annotations: { ...RENDER_ANNOTATIONS },
      outputSchema: RENDER_OUTPUT_SCHEMA,
      parameters: {
        type: 'object',
        properties: {
          why: whyParam,
          title: { type: 'string', description: 'Card title displayed at the top', default: '' },
          subtitle: { type: 'string', description: 'Subtitle displayed below the title', default: '' },
          content: { type: 'string', description: 'Main text content of the card (supports markdown)', default: '' },
          variant: {
            type: 'string',
            enum: ['default', 'outlined', 'elevated'],
            description: 'Visual style',
            default: 'default',
          },
          component_id: componentIdParam,
          canvas_id: canvasIdParam,
        },
        required: ['why'],
      },
      fireAndForget: false,
      handler: async (params) => {
        const title = (params.title as string) || '';
        const subtitle = (params.subtitle as string) || '';
        const content = (params.content as string) || '';
        const variant = (params.variant as string) || 'default';
        const rawCompId = (params.component_id as string) || '';
        const compId = rawCompId || generateId('card');
        const canvasId = (params.canvas_id as string) || 'main';

        const children: ComponentTree[] = [];
        if (content) {
          children.push({ type: 'Text', props: { content, format: 'markdown' } });
        }
        const tree: ComponentTree = {
          type: 'Card',
          props: { title, subtitle, variant },
        };
        if (children.length > 0) {
          tree.children = children;
        }
        const result = dispatchComponent(tree, compId, canvasId);
        if ('status' in result) return result;

        return {
          status: rawCompId ? 'updated' : 'rendered',
          kind: 'card',
          component_id: compId,
          canvas_id: canvasId,
          title,
          subtitle,
          variant,
          has_body: Boolean(content),
          content_chars: content.length,
          summary: title
            ? `Card "${title}"${content ? ' with body content' : ''}`
            : 'Untitled card',
          hint: renderHint('card', compId, canvasId),
        };
      },
    },

    // ── render_table ─────────────────────────────────────────────────
    {
      name: 'render_table',
      category: 'ui-render',
      disclosureGroup: 'browser_render',
      description:
        'Render a data table in the canvas panel. Columns can be objects like [{"key":"name","header":"Name"}] or simple strings like ["Name","Age"]. Rows can be objects keyed by column key, or arrays in column order. Returns the normalised `columns` (spaces → underscores, headers lowercased) so you can reference the effective keys.',
      annotations: { ...RENDER_ANNOTATIONS },
      outputSchema: RENDER_OUTPUT_SCHEMA,
      parameters: {
        type: 'object',
        properties: {
          why: whyParam,
          columns: {
            type: 'array',
            items: { type: 'object' },
            description:
              'Column definitions. Each item can be an object with "key" and "header", or a plain string (auto-converted to {key, header}).',
          },
          rows: {
            type: 'array',
            items: { type: 'object' },
            description:
              'Row data. Each item can be an object mapping column keys to values, or an array of values in column order.',
          },
          title: { type: 'string', description: 'Optional title displayed above the table', default: '' },
          sortable: { type: 'boolean', description: 'Enable column sorting', default: true },
          page_size: { type: 'integer', description: 'Rows per page for pagination', default: 10 },
          component_id: componentIdParam,
          canvas_id: canvasIdParam,
        },
        required: ['why', 'columns', 'rows'],
      },
      fireAndForget: false,
      handler: async (params) => {
        const rawColumns = params.columns as Array<unknown>;
        const rawRows = params.rows as Array<unknown>;
        const title = (params.title as string) || '';
        const sortable = params.sortable !== false;
        const pageSize = (params.page_size as number) || 10;
        const rawCompId = (params.component_id as string) || '';
        const compId = rawCompId || generateId('table');
        const canvasId = (params.canvas_id as string) || 'main';

        const columns = rawColumns.map((col) => {
          if (typeof col === 'string') {
            return { key: col.toLowerCase().replace(/\s+/g, '_'), header: col };
          }
          const obj = col as Record<string, string>;
          return {
            key: obj.key || obj.header?.toLowerCase().replace(/\s+/g, '_') || '',
            header: obj.header || obj.key || '',
          };
        });
        const rows = rawRows.map((row) => {
          if (Array.isArray(row)) {
            const record: Record<string, unknown> = {};
            columns.forEach((col, i) => {
              record[col.key] = i < row.length ? row[i] : '';
            });
            return record;
          }
          return row as Record<string, unknown>;
        });

        const children: ComponentTree[] = [];
        if (title) {
          children.push({
            type: 'Text',
            props: { content: `## ${title}`, variant: 'heading', format: 'markdown' },
          });
        }
        const tableTree: ComponentTree = {
          type: 'Table',
          props: { columns, rows, sortable, pageSize },
        };
        const tree: ComponentTree =
          children.length > 0
            ? { type: 'Card', props: {}, children: [...children, tableTree] }
            : tableTree;

        const result = dispatchComponent(tree, compId, canvasId);
        if ('status' in result) return result;

        return {
          status: rawCompId ? 'updated' : 'rendered',
          kind: 'table',
          component_id: compId,
          canvas_id: canvasId,
          title,
          sortable,
          page_size: pageSize,
          columns: columns.map((c) => ({ key: c.key, header: c.header })),
          row_count: rows.length,
          summary: `Table "${title || 'untitled'}" with ${columns.length} column${columns.length === 1 ? '' : 's'} × ${rows.length} row${rows.length === 1 ? '' : 's'}`,
          hint: renderHint('table', compId, canvasId),
        };
      },
    },

    // ── render_chart ─────────────────────────────────────────────────
    {
      name: 'render_chart',
      category: 'ui-render',
      disclosureGroup: 'browser_render',
      description:
        'Render a chart (line | bar | pie | area) in the canvas panel. Pick pie for part-of-whole, bar for categorical comparison, line for time series, area for cumulative trends. Pre-flight: x_key and y_key are validated against data[0]; if either is missing you get a structured error with available_keys. Returns { status, chart_type, data_points, values, colors_used, x_key, y_key, hint } so you can narrate exact values and colours back.',
      annotations: { ...RENDER_ANNOTATIONS },
      outputSchema: RENDER_OUTPUT_SCHEMA,
      parameters: {
        type: 'object',
        properties: {
          why: whyParam,
          chart_type: { type: 'string', enum: ['line', 'bar', 'pie', 'area'], description: 'Type of chart' },
          data: {
            type: 'array',
            items: { type: 'object' },
            description: 'List of data points. Each point must contain both x_key and y_key.',
          },
          x_key: { type: 'string', description: 'Key in data[] to use for the X axis / slice label' },
          y_key: { type: 'string', description: 'Key in data[] to use for the Y axis / slice value' },
          title: { type: 'string', description: 'Optional chart title', default: '' },
          x_label: { type: 'string', description: 'Label for X axis', default: '' },
          y_label: { type: 'string', description: 'Label for Y axis', default: '' },
          colors: {
            type: 'array',
            items: { type: 'string' },
            description: 'Optional palette. Omit to use the default 8-colour palette.',
          },
          component_id: componentIdParam,
          canvas_id: canvasIdParam,
        },
        required: ['why', 'chart_type', 'data', 'x_key', 'y_key'],
      },
      fireAndForget: false,
      handler: async (params) => {
        const chartType = params.chart_type as string;
        const data = params.data as Array<Record<string, unknown>>;
        const xKey = params.x_key as string;
        const yKey = params.y_key as string;
        const title = (params.title as string) || '';
        const xLabel = (params.x_label as string) || '';
        const yLabel = (params.y_label as string) || '';
        const colors = params.colors as string[] | undefined;
        const rawCompId = (params.component_id as string) || '';
        const compId = rawCompId || generateId('chart');
        const canvasId = (params.canvas_id as string) || 'main';

        // ── Pre-flight validation (Principle 22) ──────────────────
        if (!Array.isArray(data) || data.length === 0) {
          const err: StructuredError = {
            status: 'error',
            code: 'empty_data',
            message: 'data[] is empty — nothing to plot.',
            hint: 'Provide at least one data point as {[x_key]: value, [y_key]: value}. Example: [{"quarter": "Q1", "revenue": 120}].',
          };
          return err;
        }
        const keys = Object.keys(data[0]);
        if (!keys.includes(xKey)) {
          const err: StructuredError = {
            status: 'error',
            code: 'unknown_key',
            message: `x_key '${xKey}' is not a field on data[0].`,
            hint: 'Pick one of the available keys listed in context.available_keys, or re-shape data[] to include the intended x_key.',
            context: { available_keys: keys, missing: xKey, role: 'x_key' },
          };
          return err;
        }
        if (!keys.includes(yKey)) {
          const err: StructuredError = {
            status: 'error',
            code: 'unknown_key',
            message: `y_key '${yKey}' is not a field on data[0].`,
            hint: 'Pick one of the available keys listed in context.available_keys, or re-shape data[] to include the intended y_key.',
            context: { available_keys: keys, missing: yKey, role: 'y_key' },
          };
          return err;
        }

        const props: Record<string, unknown> = {
          type: chartType,
          data,
          xKey,
          yKey,
          title,
          xLabel,
          yLabel,
        };
        if (colors) props.colors = colors;
        const tree: ComponentTree = { type: 'Chart', props };

        const result = dispatchComponent(tree, compId, canvasId);
        if ('status' in result) return result;

        const effectiveColors =
          colors && colors.length > 0 ? colors : DEFAULT_CHART_COLORS;
        const colorsUsed =
          chartType === 'pie'
            ? effectiveColors.slice(0, data.length)
            : [effectiveColors[0]];
        const values = data
          .slice(0, 50)
          .map((d) => ({ [xKey]: d[xKey], [yKey]: d[yKey] }));
        return {
          status: rawCompId ? 'updated' : 'rendered',
          kind: 'chart',
          chart_type: chartType,
          component_id: compId,
          canvas_id: canvasId,
          title,
          x_key: xKey,
          y_key: yKey,
          x_label: xLabel,
          y_label: yLabel,
          data_points: data.length,
          values,
          values_truncated: data.length > 50,
          colors_used: colorsUsed,
          summary: `${chartType} chart${title ? ` "${title}"` : ''} with ${data.length} data point${data.length === 1 ? '' : 's'}`,
          hint: renderHint('chart', compId, canvasId),
        };
      },
    },

    // ── render_alert ─────────────────────────────────────────────────
    {
      name: 'render_alert',
      category: 'ui-render',
      disclosureGroup: 'browser_render',
      description:
        'Render an Alert banner in the canvas panel. Use severity="error" for blocking problems, "warning" for risks, "success" for confirmations, "info" for neutral context. Returns { status, component_id, severity, message, title, dismissible, hint }.',
      annotations: { ...RENDER_ANNOTATIONS },
      outputSchema: RENDER_OUTPUT_SCHEMA,
      parameters: {
        type: 'object',
        properties: {
          why: whyParam,
          message: { type: 'string', description: 'Alert message text' },
          severity: {
            type: 'string',
            enum: ['info', 'success', 'warning', 'error'],
            description: 'Alert type',
            default: 'info',
          },
          title: { type: 'string', description: 'Optional alert title', default: '' },
          dismissible: { type: 'boolean', description: 'Allow user to dismiss the alert', default: true },
          component_id: componentIdParam,
          canvas_id: canvasIdParam,
        },
        required: ['why', 'message'],
      },
      fireAndForget: false,
      handler: async (params) => {
        const message = params.message as string;
        const severity = (params.severity as string) || 'info';
        const title = (params.title as string) || '';
        const dismissible = params.dismissible !== false;
        const rawCompId = (params.component_id as string) || '';
        const compId = rawCompId || generateId('alert');
        const canvasId = (params.canvas_id as string) || 'main';

        const tree: ComponentTree = {
          type: 'Alert',
          props: { message, severity, title, dismissible },
        };
        const result = dispatchComponent(tree, compId, canvasId);
        if ('status' in result) return result;

        return {
          status: rawCompId ? 'updated' : 'rendered',
          kind: 'alert',
          component_id: compId,
          canvas_id: canvasId,
          severity,
          title,
          message,
          dismissible,
          summary: `${severity} alert${title ? ` "${title}"` : ''}: ${message}`,
          hint: renderHint('alert', compId, canvasId),
        };
      },
    },

    // ── render_text ──────────────────────────────────────────────────
    {
      name: 'render_text',
      category: 'ui-render',
      disclosureGroup: 'browser_render',
      description:
        'Render long-form text (markdown or plain) in the canvas panel. Use for material the user will want to scroll or keep visible while the conversation continues — reports, generated copy, extracted passages. Do NOT use for your direct chat replies to the user. Returns { status, component_id, variant, format, character_count, preview, hint }.',
      annotations: { ...RENDER_ANNOTATIONS },
      outputSchema: RENDER_OUTPUT_SCHEMA,
      parameters: {
        type: 'object',
        properties: {
          why: whyParam,
          content: { type: 'string', description: 'Text content to display (supports markdown)' },
          variant: {
            type: 'string',
            enum: ['body', 'heading', 'caption', 'code'],
            description: 'Text style',
            default: 'body',
          },
          format: {
            type: 'string',
            enum: ['plain', 'markdown'],
            description: 'Text format',
            default: 'markdown',
          },
          component_id: componentIdParam,
          canvas_id: canvasIdParam,
        },
        required: ['why', 'content'],
      },
      fireAndForget: false,
      handler: async (params) => {
        const content = params.content as string;
        const variant = (params.variant as string) || 'body';
        const format = (params.format as string) || 'markdown';
        const rawCompId = (params.component_id as string) || '';
        const compId = rawCompId || generateId('text');
        const canvasId = (params.canvas_id as string) || 'main';

        const tree: ComponentTree = {
          type: 'Text',
          props: { content, variant, format },
        };
        const result = dispatchComponent(tree, compId, canvasId);
        if ('status' in result) return result;

        const preview =
          content.length > 120 ? content.slice(0, 120) + '…' : content;
        return {
          status: rawCompId ? 'updated' : 'rendered',
          kind: 'text',
          component_id: compId,
          canvas_id: canvasId,
          variant,
          format,
          character_count: content.length,
          preview,
          summary: `Text block (${content.length} chars, ${variant}/${format})`,
          hint: renderHint('text', compId, canvasId),
        };
      },
    },

    // ── render_image ─────────────────────────────────────────────────
    {
      name: 'render_image',
      category: 'ui-render',
      disclosureGroup: 'browser_render',
      description:
        'Render an image in the canvas panel from a URL or data URI. Always provide meaningful `alt` text (it ends up visible if the image fails to load). Returns { status, component_id, src, alt, width, height, object_fit, hint } — the `src` echo lets you verify you referenced the intended image.',
      annotations: { ...RENDER_ANNOTATIONS },
      outputSchema: RENDER_OUTPUT_SCHEMA,
      parameters: {
        type: 'object',
        properties: {
          why: whyParam,
          src: { type: 'string', description: 'Image URL or data URI' },
          alt: { type: 'string', description: 'Alt text for accessibility' },
          width: { type: 'string', description: 'Image width (CSS value)', default: '100%' },
          height: { type: 'string', description: 'Image height (CSS value)', default: 'auto' },
          object_fit: {
            type: 'string',
            enum: ['contain', 'cover', 'fill', 'none'],
            description: 'How image should fit',
            default: 'contain',
          },
          component_id: componentIdParam,
          canvas_id: canvasIdParam,
        },
        required: ['why', 'src', 'alt'],
      },
      fireAndForget: false,
      handler: async (params) => {
        const src = params.src as string;
        const alt = params.alt as string;
        const width = (params.width as string) || '100%';
        const height = (params.height as string) || 'auto';
        const objectFit = (params.object_fit as string) || 'contain';
        const rawCompId = (params.component_id as string) || '';
        const compId = rawCompId || generateId('image');
        const canvasId = (params.canvas_id as string) || 'main';

        const tree: ComponentTree = {
          type: 'Image',
          props: { src, alt, width, height, objectFit },
        };
        const result = dispatchComponent(tree, compId, canvasId);
        if ('status' in result) return result;

        return {
          status: rawCompId ? 'updated' : 'rendered',
          kind: 'image',
          component_id: compId,
          canvas_id: canvasId,
          src,
          alt,
          width,
          height,
          object_fit: objectFit,
          summary: `Image "${alt}"`,
          hint: renderHint('image', compId, canvasId),
        };
      },
    },

    // ── render_form ──────────────────────────────────────────────────
    {
      name: 'render_form',
      category: 'ui-render',
      disclosureGroup: 'browser_render',
      description:
        'Render an interactive form in the canvas panel. The user fills it in and submits; their answers arrive later as a tool result keyed by `submit_action`. Returns { status, component_id, fields, submit_action, field_count, hint }. Store the component_id and submit_action if you intend to wait for responses.',
      annotations: { ...RENDER_ANNOTATIONS },
      outputSchema: RENDER_OUTPUT_SCHEMA,
      parameters: {
        type: 'object',
        properties: {
          why: whyParam,
          fields: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                name: { type: 'string', description: 'Field identifier' },
                type: {
                  type: 'string',
                  enum: ['text', 'email', 'password', 'number', 'textarea', 'select', 'checkbox'],
                  description: 'Field type',
                },
                label: { type: 'string', description: 'Display label' },
                placeholder: { type: 'string', description: 'Optional placeholder text' },
                required: { type: 'boolean', description: 'Whether field is required', default: false },
                options: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: { value: { type: 'string' }, label: { type: 'string' } },
                  },
                  description: 'For select type, list of options',
                },
              },
              required: ['name', 'type', 'label'],
            },
            description: 'List of form field definitions',
          },
          submit_label: { type: 'string', description: 'Label for submit button', default: 'Submit' },
          submit_action: {
            type: 'string',
            description: 'Action name sent when form is submitted',
            default: 'submit_form',
          },
          title: { type: 'string', description: 'Optional form title', default: '' },
          component_id: componentIdParam,
          canvas_id: canvasIdParam,
        },
        required: ['why', 'fields'],
      },
      fireAndForget: false,
      handler: async (params) => {
        const fields = params.fields as Array<Record<string, unknown>>;
        const submitLabel = (params.submit_label as string) || 'Submit';
        const submitAction = (params.submit_action as string) || 'submit_form';
        const title = (params.title as string) || '';
        const rawCompId = (params.component_id as string) || '';
        const compId = rawCompId || generateId('form');
        const canvasId = (params.canvas_id as string) || 'main';

        const children: ComponentTree[] = [];
        if (title) {
          children.push({
            type: 'Text',
            props: { content: `## ${title}`, variant: 'heading', format: 'markdown' },
          });
        }
        const formTree: ComponentTree = {
          type: 'Form',
          props: {
            fields,
            submitLabel,
            onSubmit: { action: submitAction },
          },
        };
        const tree: ComponentTree =
          children.length > 0
            ? { type: 'Card', props: {}, children: [...children, formTree] }
            : formTree;

        const result = dispatchComponent(tree, compId, canvasId);
        if ('status' in result) return result;

        return {
          status: rawCompId ? 'updated' : 'rendered',
          kind: 'form',
          component_id: compId,
          canvas_id: canvasId,
          title,
          submit_label: submitLabel,
          submit_action: submitAction,
          fields: fields.map((f) => ({
            name: f.name,
            type: f.type,
            label: f.label,
            required: Boolean(f.required),
          })),
          field_count: fields.length,
          summary: `Form "${title || 'untitled'}" with ${fields.length} field${fields.length === 1 ? '' : 's'}`,
          hint: renderHint('form', compId, canvasId),
        };
      },
    },

    // ── canvas_info (read-only inventory) ─────────────────────────────
    {
      name: 'canvas_info',
      category: 'ui-render',
      disclosureGroup: 'browser_render',
      description:
        'Read-only inventory of the canvas surface. Actions:\n' +
        '  - list: enumerate all canvases with component counts, active flag, panel visibility. No params.\n' +
        '  - list_components(canvas_id?, limit?, offset?): components on one canvas with id, kind, title, status.\n' +
        'Use before deciding to update vs create, or to recover component_ids you forgot across turns.',
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
      outputSchema: {
        type: 'object',
        properties: {
          status: { type: 'string' },
          hint: { type: 'string' },
        },
        required: ['status'],
      },
      parameters: {
        type: 'object',
        properties: {
          why: whyParam,
          action: {
            type: 'string',
            enum: ['list', 'list_components'],
            description: 'Which query to run.',
          },
          canvas_id: {
            type: 'string',
            description: 'Target canvas for list_components. Default "main". Empty list if unknown.',
            default: 'main',
          },
          limit: {
            type: 'integer',
            description: 'Max components to return from list_components.',
            default: 50,
          },
          offset: {
            type: 'integer',
            description: 'Pagination offset for list_components.',
            default: 0,
          },
        },
        required: ['why', 'action'],
      },
      fireAndForget: false,
      handler: async (params) => {
        if (!canvasDispatch) return notMountedError();
        const action = params.action as string;
        const state = canvasDispatch.getState();

        if (action === 'list') {
          const canvases = Array.from(state.canvases.entries()).map(
            ([id, canvas]) => ({
              canvas_id: id,
              component_count: canvas.components.size,
              active: id === state.activeCanvasId,
              created_at: canvas.createdAt,
              updated_at: canvas.updatedAt,
            }),
          );
          const totalComponents = canvases.reduce(
            (n, c) => n + c.component_count,
            0,
          );
          return {
            status: 'ok',
            action,
            canvases,
            active_canvas_id: state.activeCanvasId,
            total_components: totalComponents,
            panel_visible: state.panelVisible,
            summary:
              canvases.length === 0
                ? 'No canvases rendered yet'
                : `${canvases.length} canvas${canvases.length === 1 ? '' : 'es'}, ${totalComponents} component${totalComponents === 1 ? '' : 's'} total (active: ${state.activeCanvasId ?? 'none'})`,
            hint:
              canvases.length === 0
                ? 'Nothing rendered yet. Start with render_card, render_text, or render_chart — the canvas is created implicitly.'
                : `Drill into a specific canvas with canvas_info(action:'list_components', canvas_id:'${state.activeCanvasId ?? canvases[0].canvas_id}').`,
          };
        }

        if (action === 'list_components') {
          const canvasId = (params.canvas_id as string) || 'main';
          const limit = (params.limit as number) || 50;
          const offset = (params.offset as number) || 0;
          const canvas = state.canvases.get(canvasId);
          if (!canvas) {
            return {
              status: 'ok',
              action,
              canvas_id: canvasId,
              components: [],
              component_count: 0,
              summary: `Canvas '${canvasId}' has no components (or does not exist yet)`,
              hint: `Render any component with canvas_id='${canvasId}' to create it, or call canvas_info(action:'list') to see existing canvases.`,
            };
          }
          const all = Array.from(canvas.components.values());
          const slice = all.slice(offset, offset + limit);
          const components = slice.map((c) => {
            const tree = c.jsonTree as ComponentTree;
            const kind = (tree?.type || 'unknown').toLowerCase();
            const props = (tree?.props ?? {}) as Record<string, unknown>;
            const title = (props.title as string) || '';
            return {
              component_id: c.id,
              kind,
              title,
              status: c.status,
              updated_at: c.updatedAt,
            };
          });
          const hasMore = offset + slice.length < all.length;
          return {
            status: 'ok',
            action,
            canvas_id: canvasId,
            components,
            component_count: components.length,
            total_count: all.length,
            offset,
            has_more: hasMore,
            next_offset: hasMore ? offset + slice.length : null,
            active: canvasId === state.activeCanvasId,
            summary: `Canvas '${canvasId}': ${components.length}/${all.length} component${all.length === 1 ? '' : 's'} returned`,
            hint:
              all.length === 0
                ? `Canvas '${canvasId}' is empty. Render any component with canvas_id='${canvasId}'.`
                : hasMore
                  ? `More components available — call again with offset=${offset + slice.length}.`
                  : 'All components returned. Use canvas_manage(action:"remove_component", component_id:…) to remove one.',
          };
        }

        const err: StructuredError = {
          status: 'error',
          code: 'unknown_action',
          message: `Unknown action '${action}'.`,
          hint: 'Valid actions: list, list_components.',
        };
        return err;
      },
    },

    // ── canvas_manage (mutations, destructive) ───────────────────────
    {
      name: 'canvas_manage',
      category: 'ui-render',
      disclosureGroup: 'browser_render',
      description:
        'State-changing canvas operations. Destructive actions remove data. Actions:\n' +
        '  - set_active(canvas_id): switch which canvas the user sees.\n' +
        '  - clear(canvas_id?): remove all components from one canvas but keep the canvas.\n' +
        '  - close(canvas_id): remove a canvas entirely.\n' +
        '  - clear_all: nuclear reset — every canvas, every component. Use only when the user explicitly asks for a clean slate.\n' +
        '  - toggle_panel: show/hide the canvas panel in the user UI.\n' +
        '  - remove_component(component_id, canvas_id?): remove one component.\n' +
        'For read-only inventory use canvas_info.',
      annotations: {
        readOnlyHint: false,
        destructiveHint: true,
        idempotentHint: true,
        openWorldHint: false,
      },
      outputSchema: {
        type: 'object',
        properties: {
          status: { type: 'string' },
          hint: { type: 'string' },
        },
        required: ['status'],
      },
      parameters: {
        type: 'object',
        properties: {
          why: whyParam,
          action: {
            type: 'string',
            enum: [
              'set_active',
              'clear',
              'close',
              'clear_all',
              'toggle_panel',
              'remove_component',
            ],
          },
          canvas_id: {
            type: 'string',
            description:
              'Target canvas. Required for set_active and close. Optional for clear and remove_component (default "main"). Ignored for clear_all and toggle_panel.',
          },
          component_id: {
            type: 'string',
            description:
              'Required for remove_component — the same component_id returned by a prior render_* call.',
          },
        },
        required: ['why', 'action'],
      },
      fireAndForget: false,
      handler: async (params) => {
        if (!canvasDispatch) return notMountedError();
        const action = params.action as string;
        const state = canvasDispatch.getState();

        switch (action) {
          case 'set_active': {
            const canvasId = (params.canvas_id as string) || '';
            if (!canvasId) {
              const err: StructuredError = {
                status: 'error',
                code: 'missing_param',
                message: 'set_active requires canvas_id.',
                hint: 'Pass canvas_id. Use canvas_info(action:"list") to see existing canvases.',
              };
              return err;
            }
            if (!state.canvases.has(canvasId)) {
              return {
                status: 'not_found',
                action,
                canvas_id: canvasId,
                summary: `Canvas '${canvasId}' does not exist yet`,
                hint: `Canvas is implicit — render any component with canvas_id='${canvasId}' to create it, or call canvas_info(action:'list') to pick an existing one.`,
              };
            }
            canvasDispatch.setActiveCanvas(canvasId);
            return {
              status: 'active',
              action,
              active_canvas_id: canvasId,
              summary: `Active canvas switched to '${canvasId}'`,
              hint: `The user now sees '${canvasId}'. Render into it with canvas_id='${canvasId}' (or omit canvas_id since it is now the default).`,
            };
          }

          case 'clear': {
            const canvasId = (params.canvas_id as string) || 'main';
            const canvas = state.canvases.get(canvasId);
            if (!canvas) {
              return {
                status: 'not_found',
                action,
                canvas_id: canvasId,
                removed_count: 0,
                summary: `Canvas '${canvasId}' did not exist`,
                hint: 'Nothing to clear. Use canvas_info(action:"list") to see what exists.',
              };
            }
            const ids = Array.from(canvas.components.keys());
            for (const cid of ids) canvasDispatch.closeComponent(canvasId, cid);
            return {
              status: 'cleared',
              action,
              canvas_id: canvasId,
              removed_count: ids.length,
              removed_component_ids: ids,
              summary: `Cleared canvas '${canvasId}' (${ids.length} component${ids.length === 1 ? '' : 's'} removed)`,
              hint: `Canvas '${canvasId}' still exists and is empty. Render fresh components or canvas_manage(action:"close", canvas_id:"${canvasId}") to remove it entirely.`,
            };
          }

          case 'close': {
            const canvasId = (params.canvas_id as string) || '';
            if (!canvasId) {
              const err: StructuredError = {
                status: 'error',
                code: 'missing_param',
                message: 'close requires canvas_id.',
                hint: 'Pass canvas_id. To remove everything, use action:"clear_all" instead.',
              };
              return err;
            }
            const existed = state.canvases.has(canvasId);
            if (existed) canvasDispatch.closeCanvas(canvasId);
            return {
              status: existed ? 'closed' : 'not_found',
              action,
              canvas_id: canvasId,
              summary: existed
                ? `Closed canvas '${canvasId}'`
                : `Canvas '${canvasId}' did not exist`,
              hint: 'Use canvas_info(action:"list") to see remaining canvases.',
            };
          }

          case 'clear_all': {
            const canvasCount = state.canvases.size;
            const totalComponents = Array.from(state.canvases.values()).reduce(
              (n, c) => n + c.components.size,
              0,
            );
            canvasDispatch.clearAllCanvases();
            return {
              status: 'cleared_all',
              action,
              canvas_count: canvasCount,
              total_components_removed: totalComponents,
              summary: `Cleared all canvases (${canvasCount} canvas${canvasCount === 1 ? '' : 'es'}, ${totalComponents} component${totalComponents === 1 ? '' : 's'} removed)`,
              hint: 'The canvas is now empty. Render the next component to rebuild.',
            };
          }

          case 'toggle_panel': {
            const before = state.panelVisible;
            canvasDispatch.togglePanel();
            const after = !before;
            return {
              status: 'ok',
              action,
              panel_visible: after,
              summary: after ? 'Canvas panel shown' : 'Canvas panel hidden',
              hint: after
                ? 'The user can now see the canvas.'
                : 'The canvas is hidden but components are not removed — toggle again to restore.',
            };
          }

          case 'remove_component': {
            const compId = (params.component_id as string) || '';
            const canvasId = (params.canvas_id as string) || 'main';
            if (!compId) {
              const err: StructuredError = {
                status: 'error',
                code: 'missing_param',
                message: 'remove_component requires component_id.',
                hint: 'Pass component_id from a prior render_* call. Use canvas_info(action:"list_components") to recover IDs.',
              };
              return err;
            }
            const artifactId = generateId('artifact');
            canvasDispatch.handleUpdate({
              canvasId,
              componentId: compId,
              artifactId,
              action: 'remove',
            });
            canvasDispatch.handleComplete({
              canvasId,
              componentId: compId,
              artifactId,
              action: 'remove',
            });
            return {
              status: 'removed',
              action,
              kind: 'component',
              component_id: compId,
              canvas_id: canvasId,
              summary: `Removed component '${compId}' from canvas '${canvasId}'`,
              hint: 'Safe to call again — subsequent calls with the same component_id are no-ops.',
            };
          }

          default: {
            const err: StructuredError = {
              status: 'error',
              code: 'unknown_action',
              message: `Unknown action '${action}'.`,
              hint: 'Valid actions: set_active, clear, close, clear_all, toggle_panel, remove_component.',
            };
            return err;
          }
        }
      },
    },
  ];
}
